import { createStore, type StateCreator, type StoreApi } from '@bomdisoft/store/vanilla';

export type SnackbarVariant = 'default' | 'info' | 'success' | 'warning' | 'error';

export type SnackbarAnchorOrigin = {
  horizontal: 'left' | 'center' | 'right';
  vertical: 'top' | 'bottom';
};

export type SnackbarSurface = 'inverse' | 'paper' | 'tint';

export type SnackbarIndicator = 'dot' | 'icon' | 'none' | 'rail';

export type SnackbarKey = number;

export type SnackbarQueueOptions<Message> = {
  variant?: SnackbarVariant;
  autoHideDuration?: number;
  persist?: boolean;
  anchorOrigin?: SnackbarAnchorOrigin;
  customAction?: () => void;
  customActionText?: string;
  description?: Message;
  surface?: SnackbarSurface;
  indicator?: SnackbarIndicator;
};

export type SnackbarQueueItem<Message> = Required<Pick<SnackbarQueueOptions<Message>, 'indicator' | 'surface' | 'variant'>> & {
  id: SnackbarKey;
  message: Message;
  description?: Message;
  autoHideDuration: number | null;
  anchorOrigin: SnackbarAnchorOrigin;
  customAction?: () => void;
  customActionText?: string;
};

export type SnackbarQueue<Message> = {
  items: SnackbarQueueItem<Message>[];
  enqueue: (message: Message, options?: SnackbarQueueOptions<Message>) => SnackbarKey;
  close: (id?: SnackbarKey) => void;
};

const MAX_SNACKS = 5;

export const createSnackbarQueueState = <Message>(): StateCreator<SnackbarQueue<Message>> => {
  let nextId = 1;
  return (set, get) => ({
    close: (id?: SnackbarKey) => {
      set(state => ({ items: id === undefined ? [] : state.items.filter(item => item.id !== id) }));
    },
    enqueue: (message: Message, options?: SnackbarQueueOptions<Message>) => {
      const existing = get().items.find(item => item.message === message);
      if (existing) return existing.id;
      const id = nextId++;
      const item: SnackbarQueueItem<Message> = {
        anchorOrigin: options?.anchorOrigin ?? { horizontal: 'right', vertical: 'top' },
        autoHideDuration: options?.persist ? null : (options?.autoHideDuration ?? 3500),
        customAction: options?.customAction,
        customActionText: options?.customActionText,
        description: options?.description,
        id,
        indicator: options?.indicator ?? 'dot',
        message,
        surface: options?.surface ?? 'paper',
        variant: options?.variant ?? 'default',
      };
      set(state => ({ items: [...state.items.slice(-(MAX_SNACKS - 1)), item] }));
      return id;
    },
    items: [],
  });
};

export const createSnackbarQueue = <Message>(): StoreApi<SnackbarQueue<Message>> => createStore<SnackbarQueue<Message>>()(createSnackbarQueueState<Message>());

export const toSnackbarAnchor = ({ horizontal, vertical }: SnackbarAnchorOrigin) => `${vertical}-${horizontal}`;

export const groupSnackbarsByAnchor = <Message>(items: readonly SnackbarQueueItem<Message>[]) => [...new Set(items.map(item => toSnackbarAnchor(item.anchorOrigin)))].map(anchor => ({ anchor, items: items.filter(item => toSnackbarAnchor(item.anchorOrigin) === anchor) }));
